/**
 * Le player de cours — l'écran central de la phase 5 (docs/23).
 *
 * Trois états par leçon, tous **calculés par le serveur** : verrouillé,
 * disponible (ou en cours), terminé. L'écran ne rejoue aucune de ces règles ; il
 * les affiche, et grise ce qui est fermé. La barrière elle-même est ailleurs :
 * chaque leçon verrouillée ici a sa garde d'API en face, et c'est elle qui
 * refuse si l'on appelle la route directement (docs/07 §8).
 */
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useCourseBySlug, useLesson } from "@/features/catalog/api/useCatalog";
import { RichContent } from "@/shared/richtext/RichContent";
import { asDocument } from "@/shared/richtext/document";
import { AttemptRunner } from "@/features/learning/components/AttemptRunner";
import { LessonStateBadge, LockReason } from "@/features/learning/components/LessonStateBadge";
import {
  useCompleteLesson,
  useCourseProgress,
  useEnrol,
  useExerciseState,
  useOpenLesson,
  type LessonProgress,
} from "@/features/learning/api/useLearning";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function CoursePlayerPage() {
  const { t } = useTranslation("learning");
  const { slug } = useParams({ from: "/learn/learn/$slug" });
  const course = useCourseBySlug(slug);
  const courseId = course.data?.course.id;
  const progress = useCourseProgress(courseId);
  const enrol = useEnrol();

  if (course.isPending) {
    return <Skeleton className="mx-auto my-8 h-64 w-full max-w-4xl" label={t("player.loading")} />;
  }
  if (course.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={course.error} />
      </div>
    );
  }

  // Pas de progression = pas d'inscription. On propose de s'inscrire plutôt que
  // d'afficher une erreur : le cours est public, et l'élève est exactement au
  // bon endroit — il lui manque un geste, pas un droit.
  if (progress.isError || progress.data === undefined) {
    return (
      <div className="mx-auto w-full max-w-4xl space-y-4 px-4 py-8">
        <h1 className="text-2xl font-semibold">{course.data.course.title}</h1>
        <Card title={t("player.notEnrolledTitle")}>
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {t("player.notEnrolledBody")}
            </p>
            {enrol.isError && <ErrorState error={enrol.error} />}
            <Button
              onClick={() => {
                if (courseId !== undefined) {
                  enrol.mutate(courseId);
                }
              }}
              disabled={enrol.isPending}
            >
              {t("player.enrol")}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return <Player player={progress.data} />;
}

function Player({ player }: { player: NonNullable<ReturnType<typeof useCourseProgress>["data"]> }) {
  const { t } = useTranslation("learning");
  // La leçon ouverte par défaut est celle que **le serveur** désigne : refaire
  // ce choix ici (« la première non terminée ») donnerait un second algorithme,
  // et enverrait l'élève buter sur une porte close le jour où les deux
  // divergeraient.
  const [openLessonId, setOpenLessonId] = useState<string | undefined>(
    player.next_lesson_id ?? undefined,
  );
  const openLesson = useOpenLesson();

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold">{player.course_title}</h1>
        <ProgressBar player={player} />
      </header>

      {player.status === "completed" && <Alert tone="success">{t("player.courseCompleted")}</Alert>}

      <Card title={t("player.curriculum")}>
        <ol className="space-y-2">
          {player.lessons.map((lesson) => (
            <li
              key={lesson.lesson_id}
              className="rounded-md border border-slate-200 dark:border-slate-800"
            >
              <button
                type="button"
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
                disabled={lesson.state === "locked"}
                aria-expanded={openLessonId === lesson.lesson_id}
                onClick={() => {
                  const next = openLessonId === lesson.lesson_id ? undefined : lesson.lesson_id;
                  setOpenLessonId(next);
                  if (next !== undefined) {
                    openLesson.mutate({
                      enrollmentId: player.enrollment_id,
                      lessonId: lesson.lesson_id,
                    });
                  }
                }}
              >
                <span className="flex items-center gap-2 text-sm">
                  <span className="text-slate-500 dark:text-slate-400">{lesson.position}.</span>
                  <span>{lesson.title}</span>
                </span>
                <LessonStateBadge lesson={lesson} />
              </button>
              <div className="px-3 pb-2">
                <LockReason lesson={lesson} />
              </div>
              {openLessonId === lesson.lesson_id && lesson.state !== "locked" && (
                <OpenLesson
                  courseId={player.course_id}
                  enrollmentId={player.enrollment_id}
                  lesson={lesson}
                />
              )}
            </li>
          ))}
        </ol>
      </Card>
    </div>
  );
}

function ProgressBar({
  player,
}: {
  player: NonNullable<ReturnType<typeof useCourseProgress>["data"]>;
}) {
  const { t } = useTranslation("learning");
  const percent = Number(player.progress_pct);
  return (
    <div className="space-y-1">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        {t("player.progress", {
          done: player.completed_lesson_count,
          total: player.lesson_count,
          percent: player.progress_pct,
        })}
      </p>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("player.progressLabel")}
      >
        <div className="bg-brand-600 h-full" style={{ width: `${String(percent)}%` }} />
      </div>
    </div>
  );
}

interface OpenLessonProps {
  courseId: string;
  enrollmentId: string;
  lesson: LessonProgress;
}

function OpenLesson({ courseId, enrollmentId, lesson }: OpenLessonProps) {
  const { t } = useTranslation("learning");
  const detail = useLesson(courseId, lesson.lesson_id);
  const complete = useCompleteLesson();
  // Un seul exercice affiché : c'est ce que la phase 4 produit par leçon. Le
  // jour où il y en aura plusieurs, c'est cette ligne qui deviendra une boucle
  // — et `exercise_ids` est déjà une liste pour que ce jour-là ne demande pas
  // de changer le contrat.
  const exerciseId = lesson.exercise_ids[0];
  const exercise = useExerciseState(exerciseId);

  return (
    <div className="space-y-4 border-t border-slate-200 px-3 py-3 dark:border-slate-800">
      {detail.isPending && <Skeleton className="h-24 w-full" label={t("player.loading")} />}
      {detail.isError && <ErrorState error={detail.error} />}
      {detail.data !== undefined && <RichContent document={asDocument(detail.data.content)} />}

      {exercise.data !== undefined && <AttemptRunner exercise={exercise.data} />}

      {complete.isError && <ErrorState error={complete.error} />}
      {/* Le bouton reste actif même s'il reste un exercice bloquant à réussir :
          marquer une leçon comme lue est déclaratif, et le serveur l'accepte
          (docs/12 §5). Le griser refuserait un geste légitime. */}
      {lesson.completed_at === null && (
        <Button
          variant="secondary"
          disabled={complete.isPending}
          onClick={() => {
            complete.mutate({ enrollmentId, lessonId: lesson.lesson_id });
          }}
        >
          {t("player.markRead")}
        </Button>
      )}
    </div>
  );
}
